import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';

import { USER_MANAGEMENT_API_CONFIG, UserManagementConfig } from '../config';
import {
  AddReplyRequest,
  AdminSupportTicketListResponse,
  CreateSupportTicketRequest,
  SupportTicketDetail,
  SupportTicketDetailResponse,
  SupportTicketListResponse,
  SupportTicketPriority,
  SupportTicketStatus,
  UpdateTicketStatusRequest,
} from '../models/support-ticket.model';

export interface GetMyTicketsParams {
  status?: SupportTicketStatus;
  page?: number;
  limit?: number;
}

export interface GetAllTicketsForStaffParams {
  status?: SupportTicketStatus;
  priority?: SupportTicketPriority;
  /** MongoDB user id of the requester to filter by */
  userId?: string;
  /** Free-text match against short/long description */
  search?: string;
  page?: number;
  limit?: number;
}

/**
 * Support ticket API — self-service (create/list-own/view/reply, used by
 * stratum-app) plus admin/staff triage (list-all/update-status, used by
 * stratum-analytics).
 *
 * @remarks
 * The 4 self-service methods rely on stratum-app's `apiErrorInterceptor` to
 * normalize errors into `ApiErrorResponse` (from cms-post-lib) — no manual
 * HTTP error handling is done for them here. The 2 admin-only methods are
 * also consumed from stratum-analytics, which has no such interceptor, so
 * they catch `HttpErrorResponse` directly and throw a plain `Error`,
 * matching `PlatformAdminAnalyticsService`'s existing convention there.
 */
@Injectable({ providedIn: 'root' })
export class SupportTicketService {
  private readonly _http = inject(HttpClient);
  private readonly _config: UserManagementConfig = inject(USER_MANAGEMENT_API_CONFIG);

  private readonly _baseUrl = `${this._config.apiBaseUrl}/api/support-tickets`;

  createTicket(payload: CreateSupportTicketRequest): Observable<SupportTicketDetail> {
    return this._http
      .post<SupportTicketDetailResponse>(this._baseUrl, payload)
      .pipe(map(res => res.data));
  }

  getMyTickets(params: GetMyTicketsParams = {}): Observable<SupportTicketListResponse> {
    let httpParams = new HttpParams();
    if (params.status) httpParams = httpParams.set('status', params.status);
    if (params.page) httpParams = httpParams.set('page', params.page);
    if (params.limit) httpParams = httpParams.set('limit', params.limit);

    return this._http.get<SupportTicketListResponse>(this._baseUrl, { params: httpParams });
  }

  getTicketById(id: string): Observable<SupportTicketDetail> {
    return this._http
      .get<SupportTicketDetailResponse>(`${this._baseUrl}/${id}`)
      .pipe(map(res => res.data));
  }

  addReply(id: string, payload: AddReplyRequest): Observable<SupportTicketDetail> {
    return this._http
      .post<SupportTicketDetailResponse>(`${this._baseUrl}/${id}/replies`, payload)
      .pipe(map(res => res.data));
  }

  /** Admin/staff-only — cross-organization ticket queue. */
  getAllTicketsForStaff(params: GetAllTicketsForStaffParams = {}): Observable<AdminSupportTicketListResponse> {
    let httpParams = new HttpParams();
    if (params.status) httpParams = httpParams.set('status', params.status);
    if (params.priority) httpParams = httpParams.set('priority', params.priority);
    if (params.userId) httpParams = httpParams.set('userId', params.userId);
    if (params.search) httpParams = httpParams.set('search', params.search);
    if (params.page) httpParams = httpParams.set('page', params.page);
    if (params.limit) httpParams = httpParams.set('limit', params.limit);

    return this._http
      .get<AdminSupportTicketListResponse>(`${this._baseUrl}/admin/all`, { params: httpParams })
      .pipe(catchError(err => this._handleAdminError('getAllTicketsForStaff', err)));
  }

  /** Admin/staff-only — update status, priority and/or assignee. Only send fields that changed. */
  updateTicketStatus(id: string, payload: UpdateTicketStatusRequest): Observable<SupportTicketDetail> {
    return this._http
      .patch<SupportTicketDetailResponse>(`${this._baseUrl}/admin/${id}/status`, payload)
      .pipe(
        map(res => res.data),
        catchError(err => this._handleAdminError('updateTicketStatus', err))
      );
  }

  private _handleAdminError(operation: string, error: HttpErrorResponse): Observable<never> {
    let message: string;

    if (error.status === 0) {
      message = 'Unable to reach the server. Please check your connection.';
    } else if (error.status === 401) {
      message = 'Unauthorized. Platform administrator access required.';
    } else if (error.status === 403) {
      message = 'Forbidden. You do not have platform admin privileges.';
    } else if (error.status === 404) {
      message = 'Ticket not found.';
    } else if (error.status === 429) {
      message = 'Rate limit exceeded. Please try again later.';
    } else {
      message = error.error?.message ?? `Failed to complete "${operation}". Please try again.`;
    }

    return throwError(() => new Error(message));
  }
}
